/**
 * WHO AM I, and WHAT AM I ALLOWED TO DO.
 *
 * UF_00 section 5 calls progressive verification gating "the single most important cross panel rule,
 * because it decides what every screen can and cannot do."
 *
 * A rule that important must have exactly one source of truth, and it must not be the client.
 *
 * ## The tier is fetched, never cached, never inferred
 *
 * It would be easy to stash `tier` in localStorage at sign-in and read it from there. Do that and the
 * app is wrong the moment a compliance officer changes it — which is the exact moment being right
 * matters most. A participant put on a screening hold at 14:00 would go on seeing a Send button, and
 * pressing it would produce a refusal they had been given no warning of.
 *
 * So the tier comes from `/auth/me`, on boot, from the server that actually knows. The only thing
 * this client holds is the opaque session token, which is the one thing it cannot make up.
 *
 * ## And the gate is a COURTESY, not a control
 *
 * Every gate in this app is a nicety on top of a real refusal. `TierGate` hides a button; the
 * BACKEND refuses the movement (FR-X-ID-006), the database refuses the write, and neither of them
 * asks this file's opinion. If a determined user edits their way past every gate here, they reach an
 * endpoint that says no.
 *
 * That is the correct relationship, and it is worth being explicit about, because the failure mode of
 * forgetting it is a platform whose security is a `useState` in a browser.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { api, NotSignedInError, type Me } from './api';
import { clearToken, getToken } from './session';
import type { Tier } from './domain';

// Tier is defined ONCE, in domain.ts. A second copy here would be a second answer to the most
// important question in the app, and the two would eventually differ by one letter at 2am.
export type { Tier };

/** The ladder, in order. Comparisons below depend on this ordering and nothing else. */
const LADDER: Tier[] = ['T0', 'T1', 'T2', 'T3'];

export function atLeast(current: Tier, required: Tier): boolean {
  return LADDER.indexOf(current) >= LADDER.indexOf(required);
}

interface SessionState {
  me: Me | null;
  /** Still finding out. Render skeletons, NOT a signed-out state: they are different things. */
  loading: boolean;
  signedIn: boolean;
  tier: Tier;
  /** Re-fetch from the server. Call after anything that could change the tier or the status. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setMe(null);
      setLoading(false);
      return;
    }

    try {
      setMe(await api.me());
    } catch (e) {
      // A 401 means the session is gone: the token is already cleared by the API client, and the
      // honest thing to render is signed-out. Anything else is a real failure and must NOT be
      // mistaken for one — silently signing a user out because the network hiccuped is maddening.
      if (e instanceof NotSignedInError) {
        setMe(null);
      } else {
        throw e;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh().catch(() => setLoading(false));
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Local state is cleared EVEN IF the call failed. A user who pressed "sign out" on a shared
      // machine must not still be signed in because the network was down.
      clearToken();
      setMe(null);
      navigate('/');
    }
  }, [navigate]);

  const value = useMemo<SessionState>(
    () => ({
      me,
      loading,
      signedIn: me !== null,
      // T0 is the safe default while we do not know. Assuming a HIGHER tier than the truth would
      // show a user actions they cannot take; assuming a lower one shows a gate that goes away.
      tier: (me?.tier ?? 'T0') as Tier,
      refresh,
      signOut,
    }),
    [me, loading, refresh, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession must be used inside a SessionProvider');
  return ctx;
}
